import { RewardIcon } from '@ui/shared/icons/RewardIcon';
import type { HabitCategory } from '@contracts';

/**
 * The badge geometry system (badges-and-celebrations.md SS1): a shared
 * rounded-hexagon frame across every badge so a wall of badges reads as a
 * system, not a sticker album. 96x96 viewBox, scales to any rendered size.
 *
 * Earned = luminous (vivid-tier gradient stroke, tinted field, glow halo).
 * Locked/in-progress = quiet outline (border.strong stroke, transparent
 * field, muted icon) with a progress arc along the hexagon's own perimeter.
 *
 * Category accent = the linked habit's category (null for habit-independent
 * templates, which render "silver" — text.primary-based, per SS1.2's
 * ratified silver-not-gold rule). Phase 2a's active badges
 * (accepted_recalibrations, action_completions) are ALL habit-independent,
 * so `category` is always null in this build's actual usage today — the
 * category prop stays wired for when habit_completions/clean_weeks badges
 * become creatable (Phase 2b+, once habits exist).
 */
export function BadgeGraphic({
  iconId,
  earned,
  category,
  progressFraction,
  size = 64,
}: {
  readonly iconId: string;
  readonly earned: boolean;
  readonly category: HabitCategory | null;
  /** 0-1; ignored when earned (earned badges show a full luminous frame, not an arc). */
  readonly progressFraction: number;
  readonly size?: number;
}) {
  const accentVar = category ? categoryVividVar(category) : 'var(--text-primary)';
  const accentTextVar = category ? categoryTextVar(category) : 'var(--text-primary)';

  // Rounded hexagon path, flat-top, inscribed in an 88x88 box centered in the
  // 96x96 viewBox (SS1.1 anatomy). Corner radius approximated with quadratic
  // curves at each vertex — a hand-rolled rounded-hex path since SVG has no
  // native rounded-polygon primitive.
  const hexPath =
    'M 28 8 L 68 8 Q 74 8 77 13 L 92 40 Q 95 45 92 51 L 77 78 Q 74 83 68 83 L 28 83 Q 22 83 19 78 L 4 51 Q 1 45 4 40 L 19 13 Q 22 8 28 8 Z';
  // Perimeter approx length for the progress-arc dash math (measured via a
  // detached SVG in dev tools against the path above; kept as a constant
  // rather than measured at runtime with getTotalLength() so this component
  // has no DOM-measurement effect and renders identically in SSR/tests).
  const perimeter = 320;
  const clamped = Math.max(0, Math.min(1, progressFraction));
  const dashOffset = perimeter - perimeter * clamped;

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg viewBox="0 0 96 96" width={size} height={size} aria-hidden="true">
        {earned ? (
          <defs>
            <linearGradient id={`badge-frame-${iconId}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={accentVar} />
              <stop offset="100%" stopColor="color-mix(in srgb, white 35%, currentColor)" />
            </linearGradient>
          </defs>
        ) : null}

        {/* Field */}
        <path
          d={hexPath}
          fill={earned ? `color-mix(in srgb, ${accentVar} 14%, var(--surface-raised))` : 'transparent'}
        />

        {/* Frame */}
        <path
          d={hexPath}
          fill="none"
          stroke={earned ? accentVar : 'var(--border-strong)'}
          strokeWidth={earned ? 3 : 1.5}
          style={{ color: accentVar }}
        />

        {/* Progress arc (locked state only) */}
        {!earned && clamped > 0 && (
          <path
            d={hexPath}
            fill="none"
            stroke={accentVar}
            strokeWidth={3}
            strokeLinecap="butt"
            strokeDasharray={perimeter}
            strokeDashoffset={dashOffset}
          />
        )}
      </svg>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RewardIcon
          iconId={iconId}
          size={Math.round(size * 0.34)}
          color={earned ? accentVar : 'var(--text-muted)'}
          strokeWidth={2}
        />
      </div>
      {/* accentTextVar reserved for a future text-tier caption rendered
          inside this component; currently unused directly (callers render
          their own caption text alongside), kept as a documented lookup so
          BadgeCell doesn't need its own duplicate category->text-token map. */}
      <span style={{ display: 'none' }}>{accentTextVar}</span>
    </div>
  );
}

function categoryVividVar(category: HabitCategory): string {
  switch (category) {
    case 'health':
      return 'var(--accent-health-vivid)';
    case 'self_care':
      return 'var(--accent-selfcare-vivid)';
    case 'study':
      return 'var(--accent-study-vivid)';
    case 'other':
      return 'var(--accent-other-vivid)';
    default: {
      const exhaustive: never = category;
      return exhaustive;
    }
  }
}

function categoryTextVar(category: HabitCategory): string {
  switch (category) {
    case 'health':
      return 'var(--accent-health)';
    case 'self_care':
      return 'var(--accent-selfcare)';
    case 'study':
      return 'var(--accent-study)';
    case 'other':
      return 'var(--accent-other)';
    default: {
      const exhaustive: never = category;
      return exhaustive;
    }
  }
}
